import {
  Avatar,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import isArray from 'lodash/isArray'
import { useCallback, useMemo, useState } from 'react'
import { EmphasizedNumber, InformationModal, ItemImageArea } from 'utoniq-ui'

import {
  ItemCollectContentItem,
  Maybe,
  MissionLabel as EnumMissionLabel,
  MultipleItemsMissionFragment,
} from '@/clients/utoniq-core/schema'
import { MissionLabel } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

type ItemCollect = Pick<ItemCollectContentItem, 'requiredCount'> & {
  item: MultipleItemsMissionFragment
}

export type ItemsMissionBoxProps = {
  missionId: string
  item: ItemCollect | ItemCollect[]
  number: number
  label: Maybe<EnumMissionLabel>
  formattedExpiredAt: string
  onSubmitAnswerItem: (missionId: string) => Promise<undefined | number>
}

export const ItemsMissionBox: Organism<ItemsMissionBoxProps> = ({
  missionId,
  label,
  number,
  item,
  formattedExpiredAt,
  onSubmitAnswerItem,
}) => {
  const { t } = useTranslation('itemMissions')
  const { showError } = useMessage()
  const [loading, setLoading] = useState<boolean>(false)
  const [requiredCount, setRequiredCount] = useState<number>(0)
  const [open, setOpen] = useState<boolean>(false)
  const items = useMemo<ItemCollect[]>(
    () => (isArray(item) ? item : [item]),
    [item]
  )

  const handleSubmitAnswerItem = useCallback(async () => {
    setLoading(true)
    setRequiredCount(0)
    await onSubmitAnswerItem(missionId)
      .then(requiredCount => {
        if (requiredCount) {
          setRequiredCount(requiredCount)
          setOpen(true)
        }
      })
      .catch(showError)
    setLoading(false)
  }, [missionId, onSubmitAnswerItem, showError])

  return (
    <Card
      elevation={0}
      sx={{ mt: 3, position: 'relative', overflow: 'visible' }}
    >
      <MissionLabel
        label={label}
        sx={{
          position: 'absolute',
          top: -10,
          left: 20,
          zIndex: 1,
          width: 72,
          height: 20,
        }}
      />
      <CardContent component={Stack} flex={1} sx={{ p: 5 }}>
        <Stack spacing={3}>
          {items.map(({ item: missionItem, requiredCount }) => (
            <Stack direction="row" spacing={4} key={missionItem.id}>
              <ItemImageArea
                src={missionItem.thumbnail.front?.smallUri ?? ''}
                size="x-small"
              />
              <Stack
                flex={1}
                justifyContent="space-between"
                sx={{ minWidth: 0 }}
              >
                <Stack direction="row" alignItems="center">
                  <Stack flex={1} sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={styleSets.lineCamp(1)}>
                      {missionItem.title}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {missionItem.rarityIconImage?.smallUri && (
                        <Avatar
                          variant="square"
                          src={missionItem.rarityIconImage.smallUri}
                          sx={{
                            height: 12,
                            width: 12,
                          }}
                        />
                      )}
                      <Typography variant="overline">
                        {missionItem.rarity?.name
                          ? t(missionItem.rarity.name)
                          : '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <EmphasizedNumber variant="h1">
                      {requiredCount}
                    </EmphasizedNumber>
                    <Typography variant="subtitle1">{t('sheet')}</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('reward')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t('gachaTicket', { number })}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          ))}
        </Stack>
        <Stack sx={{ pt: 5 }} spacing={3} alignItems="center">
          <Button
            size="large"
            sx={{ width: 244 }}
            loading={loading}
            onClick={handleSubmitAnswerItem}
            disabled={
              label === EnumMissionLabel.Clear ||
              label === EnumMissionLabel.Expired
            }
          >
            {t('toEarn')}
          </Button>
          <Stack direction="row" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {t('expirationDate')}
            </Typography>
            <Typography variant="caption">
              {t('expiration', { date: formattedExpiredAt })}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
      <InformationModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('noItemConfirmTitle')}
        content={t('noItemConfirmDesc', { number: requiredCount })}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            onClick={() => setOpen(false)}
          >
            {t('ok')}
          </Button>
        }
      />
    </Card>
  )
}
